import router from './router'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { useUserStoreWithOut } from '@/store/modules/user'
import { getMenuListApi } from '@/api/menu'
import { Layout } from '@/utils/routerHelper'

const ensureSlashPrefix = (path: string): string => {
  return path.startsWith('/') ? path : '/' + path
}

const { start, done } = useNProgress()
const { loadStart, loadDone } = usePageLoading()
const whiteList = [
  '/',
  '/auth',
  '/auth/login',
  '/auth/register',
  '/confirm-email',
  '/password-change',
  '/redirect',
  '/404'
]

let isLoadingMenus = false

const getFirstMenuPath = (userMenus: any[]): string => {
  if (userMenus && userMenus.length > 0) {
    const sortedMenus = [...userMenus].sort((a: any, b: any) => (b.order ?? 0) - (a.order ?? 0))
    const firstMenu = sortedMenus.find((menu: any) => menu.component)
    if (firstMenu) {
      return ensureSlashPrefix(firstMenu.path)
    }
  }
  return ''
}

const buildMenuRoutes = (userMenus: any[]) => {
  const pageModules = import.meta.glob('./views/**/*.{vue,tsx}')

  return userMenus
    .filter((menu: any) => menu.component)
    .map((menu: any) => {
      const fileName = menu.component.split('/').pop()
      const globKey = Object.keys(pageModules).find((key) => key.endsWith(`/${fileName}`))
      const comModule = globKey ? pageModules[globKey] : undefined
      if (!comModule) {
        console.warn(
          '[permission.ts] 未找到组件文件:',
          fileName,
          'menu:',
          menu.name,
          '可用keys=',
          Object.keys(pageModules)
        )
      }
      const isAgentTemplate = fileName === 'AgentTemplate.vue'
      const routeName = menu.path
        .replace(/^\/+/, '')
        .replace(/[-/]/g, ' ')
        .replace(/\s(\w)/g, (_, c: string) => c.toUpperCase())
        .replace(/^./, (s: string) => 'Menu' + s.toUpperCase())
      return {
        path: ensureSlashPrefix(menu.path),
        component: Layout,
        name: routeName,
        meta: {
          title: menu.name,
          icon: menu.icon,
          order: menu.order ?? 0,
          ...(isAgentTemplate ? { fullHeight: true } : {})
        },
        children: [
          {
            path: '',
            component: comModule,
            name: routeName + 'Index',
            meta: {
              title: menu.name,
              icon: menu.icon || '',
              noCache: true,
              agentId: menu.agentId || '',
              ...(isAgentTemplate ? { fullHeight: true } : {})
            }
          }
        ]
      }
    })
    .sort((a: any, b: any) => (b.meta.order ?? 0) - (a.meta.order ?? 0))
}

const loadMenusAndAddRoutes = async (): Promise<string> => {
  const permissionStore = usePermissionStoreWithOut()
  let firstMenuPath = ''

  if (!permissionStore.getIsAddRouters) {
    if (isLoadingMenus) {
      console.log('[permission.ts] 菜单正在加载中，跳过重复请求')
      return firstMenuPath
    }

    isLoadingMenus = true
    try {
      const menuRes = await getMenuListApi()
      const userMenus = menuRes.data || []

      firstMenuPath = getFirstMenuPath(userMenus)

      const menuRoutes = buildMenuRoutes(userMenus)

      await permissionStore
        .generateRoutes('server', menuRoutes as unknown as AppCustomRouteRecordRaw[])
        .catch(() => {})
      permissionStore.getAddRouters.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw)
      })
      permissionStore.setIsAddRouters(true)
    } catch (error) {
      console.error('获取菜单失败，使用静态路由', error)
      await permissionStore.generateRoutes('static').catch(() => {})
    } finally {
      isLoadingMenus = false
    }
  } else {
    const routers = permissionStore.getRouters
    if (routers && routers.length > 0) {
      const sortedRouters = [...routers].sort(
        (a: any, b: any) => (b.meta?.order ?? 0) - (a.meta?.order ?? 0)
      )
      const firstRouter = sortedRouters.find((r: any) => r.meta?.title && !r.meta?.hidden)
      if (firstRouter) {
        firstMenuPath = firstRouter.path
      }
    }
  }

  return firstMenuPath
}

const getTargetPathForLoggedIn = (firstMenuPath: string): string => {
  if (firstMenuPath) {
    console.log('[permission.ts] 重定向到第一个菜单:', firstMenuPath)
    return firstMenuPath
  } else {
    const fallbackPath = '/personal/personal-center'
    console.log('[permission.ts] 无菜单，重定向到个人中心:', fallbackPath)
    return fallbackPath
  }
}

router.beforeEach(async (to, _from, next) => {
  start()
  loadStart()

  const userStore = useUserStoreWithOut()
  const permissionStore = usePermissionStoreWithOut()
  const token = userStore.getToken

  if (
    to.path === '/' ||
    to.path === '/auth' ||
    to.path === '/auth/login' ||
    to.path === '/auth/register'
  ) {
    if (token) {
      const firstMenuPath = await loadMenusAndAddRoutes()
      const targetPath = getTargetPathForLoggedIn(firstMenuPath)
      next({ path: targetPath, replace: true })
    } else {
      next()
    }
    return
  }

  if (whiteList.includes(to.path)) {
    next()
    return
  }

  if (!token) {
    localStorage.setItem('redirectAfterLogin', to.fullPath)
    next({ path: '/auth/login', replace: true })
    return
  }

  if (!permissionStore.getIsAddRouters) {
    const firstMenuPath = await loadMenusAndAddRoutes()

    if (to.path === '/' || whiteList.includes(to.path)) {
      const targetPath = getTargetPathForLoggedIn(firstMenuPath)
      next({ path: targetPath, replace: true })
    } else {
      next({ ...to, replace: true })
    }
  } else {
    next()
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done()
  loadDone()
})
